import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { resetAdminClientState } from '@/lib/adminAuthState';

// Dev-only auth logger — keeps production console clean and reduces Sentry
// breadcrumb noise. Errors and warnings still go through console directly.
const authLog: (...args: unknown[]) => void = import.meta.env["DEV"]
  ? (...args) => console.log(...args)
  : () => {};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isEmailVerified: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: Error | null; needsVerification: boolean; emailConfirmed?: boolean; emailDispatchStatus?: 'confirmed' | 'failed' | 'unconfirmed' }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: Error | null }>;
  signInWithApple: (redirectPath?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SIGN_OUT_TIMEOUT_MS = 10000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const isSigningOutRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);
  const loadingRef = useRef(true);

  const isEmailVerified = user?.email_confirmed_at != null;

  // Keep ref in sync so safety timeout can check real state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const getStoredSessionTokenInfo = () => {
    const authKey = `sb-${import.meta.env["VITE_SUPABASE_PROJECT_ID"]}-auth-token`;

    try {
      const raw = localStorage.getItem(authKey);
      return {
        authKey,
        hasToken: !!raw,
        tokenLength: raw?.length ?? 0,
      };
    } catch {
      return {
        authKey,
        hasToken: false,
        tokenLength: 0,
      };
    }
  };

  useEffect(() => {
    let hasAuthStateEvent = false;
    let isRecoveringSession = false;

    const applySessionState = (nextSession: Session | null) => {
      sessionRef.current = nextSession;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
      // Identify the user to Sentry (lazy-loaded; safe before init).
      void import("@/integrations/monitoring/sentry").then((m) =>
        m.setSentryUser(nextSession?.user ? { id: nextSession.user.id, ...(nextSession.user.email ? { email: nextSession.user.email } : {}) } : null),
      ).catch(() => {});
    };

    // Safety timeout: if auth never resolves within 8 seconds, force loading=false
    const safetyTimeout = window.setTimeout(() => {
      if (loadingRef.current) {
        console.warn('[Auth] Safety timeout reached — forcing loading=false');
        setLoading(false);
      }
    }, 4000);

    const recoverLostSession = () => {
      if (isRecoveringSession || isSigningOutRef.current) return;
      isRecoveringSession = true;

      window.setTimeout(async () => {
        try {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshData?.session) {
            authLog('[Auth] Session recovered successfully');
            applySessionState(refreshData.session);
            return;
          }
          if (refreshError) {
            console.warn('[Auth] Session recovery failed:', refreshError.message);
            if (refreshError.message?.includes('Refresh Token') || refreshError.message?.includes('refresh_token')) {
              authLog('[Auth] Invalid refresh token, signing out cleanly');
              await supabase.auth.signOut({ scope: 'local' });
            }
          }
          // Always resolve loading after recovery attempt
          applySessionState(null);
        } catch (e) {
          console.warn('[Auth] Session recovery error:', e);
          applySessionState(null);
        } finally {
          isRecoveringSession = false;
        }
      }, 0);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        hasAuthStateEvent = true;
        const storageInfo = getStoredSessionTokenInfo();

        authLog('[Auth] State change:', event, !!nextSession, 'user:', nextSession?.user?.id ?? 'none', 'isSigningOut:', isSigningOutRef.current);
        authLog('[Auth] State change token snapshot:', storageInfo);

        if (nextSession?.access_token) {
          authLog('[Auth] Access token present, length:', nextSession.access_token.length);
        }

        // Never run blocking auth calls synchronously inside the auth state callback.
        if (event === 'USER_UPDATED' && nextSession?.user) {
          applySessionState(nextSession);

          window.setTimeout(async () => {
            try {
              const { data, error } = await supabase.auth.getUser();
              if (error) {
                console.warn('[Auth] USER_UPDATED getUser failed:', error.message);
                return;
              }

              if (data?.user) {
                setUser(data.user);
                authLog('[Auth] USER_UPDATED synchronized user:', {
                  email: data.user.email,
                  pendingNewEmail: (data.user as { new_email?: string }).new_email ?? null,
                });
              }
            } catch (error) {
              console.warn('[Auth] USER_UPDATED sync failed:', error);
            }
          }, 0);

          return;
        }

        // Redirect to /reset-password on PASSWORD_RECOVERY event (even if user lands on homepage)
        if (event === 'PASSWORD_RECOVERY' && nextSession) {
          applySessionState(nextSession);
          const currentPath = window.location.pathname;
          if (currentPath !== '/reset-password') {
            authLog('[Auth] PASSWORD_RECOVERY detected, redirecting to /reset-password');
            window.location.href = '/reset-password';
          }
          return;
        }

        if (!nextSession && sessionRef.current && event !== 'SIGNED_OUT' && !isSigningOutRef.current) {
          authLog('[Auth] Session lost unexpectedly, attempting recovery...');
          recoverLostSession();
          return;
        }

        applySessionState(nextSession);
      }
    );

    supabase.auth.getSession().then(({ data: { session: initialSession }, error }) => {
      const shouldApplyInitialSession = !hasAuthStateEvent || !!initialSession;
      const storageInfo = getStoredSessionTokenInfo();

      if (error) {
        console.error('[Auth] getSession error:', error.message);
        // Handle invalid refresh token on initial load
        if (error.message?.includes('Refresh Token') || error.message?.includes('refresh_token')) {
          authLog('[Auth] Stale refresh token on init, clearing session');
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          sessionRef.current = null;
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
      }

      authLog(
        '[Auth] Initial session:',
        !!initialSession,
        'user:',
        initialSession?.user?.id ?? 'none',
        'applied:',
        shouldApplyInitialSession
      );
      authLog('[Auth] Initial session token snapshot:', storageInfo);

      if (initialSession?.access_token) {
        authLog('[Auth] Session token exists in storage, length:', initialSession.access_token.length);
      } else {
        authLog('[Auth] No session token found in storage');
      }

      if (shouldApplyInitialSession) {
        sessionRef.current = initialSession;
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      }

      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(safetyTimeout);
    };
  }, []);

  const getSafeRedirectPath = (redirectPath?: string) => {
    if (!redirectPath) return '/dashboard';

    if (redirectPath.startsWith('http://') || redirectPath.startsWith('https://')) {
      try {
        const parsed = new URL(redirectPath);
        if (parsed.origin !== window.location.origin) return '/dashboard';
        redirectPath = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        return '/dashboard';
      }
    }

    if (!redirectPath.startsWith('/') || redirectPath.startsWith('//') || redirectPath === '/') {
      return '/dashboard';
    }

    return redirectPath;
  };

  const setPostAuthRedirect = (redirectPath: string) => {
    try { sessionStorage.setItem('post_auth_redirect', redirectPath); } catch { /* no-op */ }
    try { localStorage.setItem('post_auth_redirect', redirectPath); } catch { /* no-op */ }
  };

  const clearPostAuthRedirect = () => {
    try { sessionStorage.removeItem('post_auth_redirect'); } catch { /* no-op */ }
    try { localStorage.removeItem('post_auth_redirect'); } catch { /* no-op */ }
  };

  const clearStoredAuthArtifacts = () => {
    const prefix = `sb-${import.meta.env["VITE_SUPABASE_PROJECT_ID"]}-`;

    try {
      const localKeysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localKeysToRemove.push(key);
        }
      }
      localKeysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      /* no-op */
    }

    try {
      const sessionKeysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* no-op */
    }
  };

  const buildOAuthRedirectUri = (redirectPath?: string) => {
    const safePath = getSafeRedirectPath(redirectPath);
    const params = new URLSearchParams();
    params.set('redirect', safePath);

    const previewToken = new URLSearchParams(window.location.search).get('__lovable_token');
    if (previewToken) {
      params.set('__lovable_token', previewToken);
    }

    return `${window.location.origin}/auth?${params.toString()}`;
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const redirectPath = '/onboarding';
    const redirectUrl = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectPath)}`;

    setPostAuthRedirect(redirectPath);

    authLog('[Auth] signUp started for:', normalizedEmail);
    
    const { traceFlow } = await import('@/integrations/monitoring/sentry');
    const { data, error } = await traceFlow(
      'auth.signup',
      { email_domain: normalizedEmail.split('@')[1] ?? 'unknown' },
      () => supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
          }
        }
      }),
    );

    authLog('[Auth] signUp response:', {
      hasUser: !!data.user,
      hasSession: !!data.session,
      emailConfirmed: data.user?.email_confirmed_at ?? null,
      identities: data.user?.identities?.length ?? 0,
      error: error?.message ?? null,
    });

    // Detect repeated signup: Supabase returns a user with empty identities
    // array (and no error) when the email is already registered.
    const isRepeatedSignup =
      !error &&
      !!data.user &&
      Array.isArray(data.user.identities) &&
      data.user.identities.length === 0;

    if (isRepeatedSignup) {
      authLog('[Auth] Repeated signup detected for:', normalizedEmail);
      return {
        error: new Error('An account with this email already exists. Please sign in or reset your password.') as Error,
        needsVerification: false,
      };
    }

    // Determine if email confirmation is required
    const needsVerification = !error && !!data.user && (!data.session || !data.user.email_confirmed_at);

    // Log account creation if successful
    if (!error && data.user) {
      setTimeout(() => {
        supabase.from('user_activity_logs').insert([{
          user_id: data.user!.id,
          action_type: 'account_created',
          description: 'Account was created',
          user_agent: navigator.userAgent,
        }]);
      }, 0);
    }

    const emailConfirmed = !!data.user?.email_confirmed_at;
    const emailDispatchStatus: 'confirmed' | 'failed' | 'unconfirmed' = needsVerification ? 'unconfirmed' : 'confirmed';
    
    return { error: error as Error | null, needsVerification, emailConfirmed, emailDispatchStatus };
  };

  const signIn = async (email: string, password: string, _rememberMe: boolean = true) => {
    const { traceFlow } = await import('@/integrations/monitoring/sentry');
    const { data, error } = await traceFlow(
      'auth.login',
      { email_domain: (email.split('@')[1] ?? 'unknown').toLowerCase() },
      () => supabase.auth.signInWithPassword({ email, password }),
    );

    authLog('[Auth] signInWithPassword response:', {
      hasUser: !!data.user,
      hasSession: !!data.session,
      accessTokenLength: data.session?.access_token?.length ?? 0,
      error: error?.message ?? null,
    });

    if (!error && data.user) {
      setTimeout(() => {
        supabase.from('user_activity_logs').insert([{
          user_id: data.user.id,
          action_type: 'login',
          description: 'Signed in to account',
          user_agent: navigator.userAgent,
        }]);
      }, 0);
    }

    return { error: error as Error | null };
  };

  const signInWithGoogle = async (redirectPath?: string) => {
    const safeRedirectPath = getSafeRedirectPath(redirectPath);
    setPostAuthRedirect(safeRedirectPath);

    const redirectUri = buildOAuthRedirectUri(safeRedirectPath);
    const oauthResponse = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: redirectUri,
    });

    authLog('[Auth] Google OAuth response:', {
      redirected: (oauthResponse as { redirected?: boolean }).redirected === true,
      error: oauthResponse.error?.message ?? null,
      redirectUri,
    });

    return { error: oauthResponse.error as Error | null };
  };

  const signInWithApple = async (redirectPath?: string) => {
    const safeRedirectPath = getSafeRedirectPath(redirectPath);
    setPostAuthRedirect(safeRedirectPath);

    const redirectUri = buildOAuthRedirectUri(safeRedirectPath);
    const oauthResponse = await lovable.auth.signInWithOAuth('apple', {
      redirect_uri: redirectUri,
    });

    authLog('[Auth] Apple OAuth response:', {
      redirected: (oauthResponse as { redirected?: boolean }).redirected === true,
      error: oauthResponse.error?.message ?? null,
      redirectUri,
    });

    return { error: oauthResponse.error as Error | null };
  };

  const signOut = async () => {
    isSigningOutRef.current = true;
    clearPostAuthRedirect();

    authLog('[Auth] signOut requested — performing full global sign-out');

    resetAdminClientState();

    try {
      // Use global scope to revoke refresh tokens server-side
      const { error } = await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Sign out timed out')), SIGN_OUT_TIMEOUT_MS);
        }),
      ]);

      if (error) {
        console.warn('[Auth] signOut global scope returned error, falling back to local:', error.message);
        // Fallback: at minimum clear local state
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }
    } catch (e) {
      console.warn('[Auth] signOut error (clearing local state):', e);
      // Ensure local signOut even on timeout
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    } finally {
      // 2. Clear ALL auth-related storage artifacts
      clearStoredAuthArtifacts();

      // 3. Clear any app-level cached state
      try {
        resetAdminClientState();
        // Clear any admin invite context keys
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('admin_invite_')) sessionStorage.removeItem(key);
        }
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key?.startsWith('admin_invite_')) localStorage.removeItem(key);
        }
      } catch { /* no-op */ }

      sessionRef.current = null;
      setSession(null);
      setUser(null);
      setLoading(false);
      isSigningOutRef.current = false;
      authLog('[Auth] Full session cleared — global revoke + local purge');
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isEmailVerified, signUp, signIn, signInWithGoogle, signInWithApple, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Non-throwing variant. Returns undefined when the provider is missing.
 * Used by app-root-level handlers so a stale HMR module boundary (or any
 * provider-less mount) can never blank the whole app with a thrown error.
 */
export const useAuthOptional = () => useContext(AuthContext);
